import { useSelector } from "react-redux";
import { selectIsAmountLoansComplete, selectIsBasicPlansComplete, selectIsCoverageTypeComplete, selectIsProductTypeComplete, selectIsProposalTypeComplete, selectIsPrototypeTypeComplete } from "../../helper/proposal/proposal-is-complete-form-helper";
import { BASIC_PLAN_IDS, BASIC_PLAN_TYPE_IDS, CHANNEL_TYPE_ID, COVERAGE_TYPE_IDS, LOAN_AMOUNTS_TYPE_IDS, OTHERS_INPUT_IDS, PRODUCT_TYPE_IDS, PROPOSAL_AS_IDS, PROPOSAL_STATUS, PROPOSAL_TYPE_IDS, SUB_TYPE_IDS } from "../../constant/gms-constant";
import { selectedBMSRState, selectedRidersState } from "../../features/gms-proposal-forms/attachable-rider/attachable-riders.slice";
import { useCurrentActuarialStatus, useCurrentAmountOfLoans, useCurrentBasicPlanType, useCurrentCoverageType, useCurrentOtherInput, useCurrentPolicyStatus, useCurrentProduct, useCurrentProposalStatus, useCurrentProposalType, useCurrentSubType, useCurrentUserDepartmentCode } from "./use-state-checker-proposal";
import { selectBasicPlansState, selectCoverageTypeState, selectProductTypeState, selectProposalTypeState, selectPrototypeTypeState } from "../../features/gms-proposal-forms/types-proposal-product-basic-plan/types-proposal-product-basic-plan-slice";
import { selectChannelSubEmailState, selectChannelSubNameState, selectChannelSubNumberState, selectChannelTypeState, selectCurrentStep, selectProposalAs, selectProposalCurrentGroupName, selectProposalCurrentStatus, selectProposalExpiredAt, selectProposalRenew, selectSECRegisteredName } from "../../features/gms-proposal-forms/steps-channel-proposal_as/steps-channel-proposal_as.slice";
import { selectAffiliation, selectBasicInformation, selectBasicInformationCompanyContactNumber, selectBasicInformationCompanyNumber, selectBasicInformationContactPersonNumber, selectedNatureOfBusinessCategory, selectedNatureOfBusinessItem, selectEffectiveDateGMS, selectHasAffiliation, selectTypeOfOptions } from "../../features/gms-proposal-forms/basic-information/basic-information.slice";
import { selectAgeProfile, selectFundingArrangementsState, selectMaxAge, selectMinAge, selectOtherSpecificationGC, selectOtherSpecificationToG, selectSelectedGroupClassification, selectSelectedSubType, selectSelectedTypeOfGroup } from "../../features/gms-proposal-forms/gc-tog-ea-mop/gc-tog-ea-mop-slice";
import { selectLevelRankingInputs, selectLevelRankingRiders } from "../../features/gms-proposal-forms/coverage/level-ranking/coverage-type-level-ranking.slice";
import { selectUniformCoverage, selectUniformCoverageRiders } from "../../features/gms-proposal-forms/coverage/uniform-coverage/coverage-type-uniform-coverage.slice";
import { selectCheckedSalaryRank, selectSalaryRank, selectSalaryRankRiders } from "../../features/gms-proposal-forms/coverage/salary-rank/coverage-type-by-salary-rank.slice";
import { selectAmountOfLoans, selectAmountOfLoansFAmount, selectAmountOfLoansInput, selectAmountOfLoansRiders, selectLoanPortfolio, selectMaxAmount, selectMinAmount } from "../../features/gms-proposal-forms/amount-of-loans/amount-loans.slice";
import { formatNumberWithComma } from "../../utils/format-numbers.utils";
import { selectCommissionValueState, selectFiles, selectLoanMaturityMonthsState, selectLoanMaturityTypeState, selectNotesState, selectPaymentTerm, selectServiceValueState } from "../../features/gms-proposal-forms/payment_term-loan_maturity-commission_services-notes-files/payment_term-loan_maturity-commission_services-notes-files.slice";
import { borrowerAgeOptionsState, selected6670Amount, selected6670State, selected7175Amount, selected7175State, selected7680Amount, selected7680State } from "../../features/gms-proposal-forms/age-bracket-and-benefit-amount/age-bracket-and-benefit-amount.slice";
import { DEPARTMENT_CODES } from "../../constant/department-code-constant";

export const useProposalTypeData = () => {
    // DEPARTMENT
    const isITDepartment = useCurrentUserDepartmentCode(DEPARTMENT_CODES.IT);
    const isActuarialDepartment = useCurrentUserDepartmentCode(DEPARTMENT_CODES.ACTUARIAL);
    const isMarketingGMSDepartment = useCurrentUserDepartmentCode(DEPARTMENT_CODES.MKT_GMS);
    const isEBAMDepartment = useCurrentUserDepartmentCode(DEPARTMENT_CODES.EBAM);

    // MARKETING GMS STATUS
    const isBookedProposal = useCurrentProposalStatus(PROPOSAL_STATUS.booked.value);
    const isReleaseProposal = useCurrentProposalStatus(PROPOSAL_STATUS.released.value);
    const isDraftProposal = useCurrentProposalStatus(PROPOSAL_STATUS.draft.value);
    const isPendingProposal = useCurrentProposalStatus(PROPOSAL_STATUS.pending.value);
    const isClosedProposal = useCurrentProposalStatus(PROPOSAL_STATUS.closed.value);
    const isAmendProposal = useCurrentProposalStatus(PROPOSAL_STATUS.amendActuarial.value);
    const isProposalApproved = useCurrentProposalStatus(PROPOSAL_STATUS.approved.value);
    const isAmendGMSProposal = useCurrentProposalStatus(PROPOSAL_STATUS.amendGMS.value);
    
    const isPassedComputation = useCurrentActuarialStatus(PROPOSAL_STATUS.passed.value);
    const isDeniedComputation = useCurrentActuarialStatus(PROPOSAL_STATUS.denied.value);
    const isQueuedComputation = useCurrentActuarialStatus(PROPOSAL_STATUS.queued.value);
    const isEndorsedComputation = useCurrentActuarialStatus(PROPOSAL_STATUS.endorsed.value);
    const isForApprovalComputation = useCurrentActuarialStatus(PROPOSAL_STATUS.forApproval.value);
    const isForReApprovalComputation = useCurrentActuarialStatus(PROPOSAL_STATUS.forReapproval.value);
    const isForRatingComputation = useCurrentActuarialStatus(PROPOSAL_STATUS.rating.value);
    const isForAmendingComputation = useCurrentActuarialStatus(PROPOSAL_STATUS.amendingActuarial.value);

    const isContractCreationPolicy = useCurrentPolicyStatus(PROPOSAL_STATUS.forContractCreation.value);
    const isContractInProgressPolicy = useCurrentPolicyStatus(PROPOSAL_STATUS.contractInProgress.value);
    const isForContractReviewPolicy = useCurrentPolicyStatus(PROPOSAL_STATUS.forContractReview.value);
    const isRevisionPolicy = useCurrentPolicyStatus(PROPOSAL_STATUS.forRevision.value);
    const isReadyForIssuancePolicy = useCurrentPolicyStatus(PROPOSAL_STATUS.readyForIssuance.value);
    const isIssuedPolicy = useCurrentPolicyStatus(PROPOSAL_STATUS.issued.value);

    // STEPS
    const proposalStep = useSelector(selectCurrentStep);
    // CHANNEL TYPES
    const channelType = useSelector(selectChannelTypeState);
    const channelTypeSubName = useSelector(selectChannelSubNameState);
    const channelTypeNumber = useSelector(selectChannelSubNumberState);
    const channelTypeEmail = useSelector(selectChannelSubEmailState);
    const showInputChannelType =
            channelType?.id === CHANNEL_TYPE_ID.broker ||
            channelType?.id === CHANNEL_TYPE_ID.generalAgency ||
            channelType?.id === CHANNEL_TYPE_ID.agent;
    // PROPOSAL AS (NEW/RENEW)
    const proposalAs = useSelector(selectProposalAs);
    const isForRenew = proposalAs?.id === PROPOSAL_AS_IDS.renew;
    // RENEW 
    const forRenew = useSelector(selectProposalRenew);
    const registeredNameForRenew = useSelector(selectSECRegisteredName);
    const registeredNameExpiredAt = useSelector(selectProposalExpiredAt);
    // CURRENT GROUP NAME
    const currentGroupName = useSelector(selectProposalCurrentGroupName);
    const currentStatusName = useSelector(selectProposalCurrentStatus);
    const pendingDefaultStatus = PROPOSAL_STATUS.pending.value
    // const proposalStatus = useSelector(selectedCurrentProposalStatusName);
    const currentStatus = currentStatusName?.trim()?.toLowerCase();
    const finalStatus = pendingDefaultStatus.trim().toLowerCase();
    const isPendingCurrentProposalStatus = currentStatus === finalStatus;

    // EFFECTIVE DATE
    const effectiveDateGMS = useSelector(selectEffectiveDateGMS);
    // CLIENT/GROUP BASIC INFORMATION
    const { 
        groupName,
        noOfLives,
        companyMobileNo,
        companyTelephone,
        completeBusinessAddress,
        proposalAddress,
        email,
        addressDesignation,
        designation,
        contactPersonSalutation,
        contactPersonFirstName,
        contactPersonMiddleName,
        contactPersonLastName,
        contactNumber,
        contactTelephone,
        tinNumber,
        secNumber,
    } = useSelector(selectBasicInformation);
    const basicInformation = useSelector(selectBasicInformation);
    const companyTypeNumber = useSelector(selectBasicInformationCompanyNumber);
    const companyContactNumber = useSelector(selectBasicInformationCompanyContactNumber);
    const contactPersonNumber = useSelector(selectBasicInformationContactPersonNumber);
    const isOpenTypeOfOption = useSelector(selectTypeOfOptions);
    const affiliation = useSelector(selectAffiliation);
    const hasAffiliation = useSelector(selectHasAffiliation);
    const natureOfBusinessCategory = useSelector(selectedNatureOfBusinessCategory);
    const natureOfBusinessItem = useSelector(selectedNatureOfBusinessItem);
    // GROUP CLASSIFICATION
    const groupClassification = useSelector(selectSelectedGroupClassification);
    // const othersGroupClassification = useSelector(selectOtherSpecificationGC);
    const othersGroupClassificationValue = useSelector(selectOtherSpecificationGC);
    const hasOtherSpecifyGC = useCurrentOtherInput(OTHERS_INPUT_IDS.groupClassification, 'gc');
    const hasOtherSpecifyBT = useCurrentOtherInput(OTHERS_INPUT_IDS.businessType, 'bt');
    // TYPE OF GROUP
    const typeOfGroup = useSelector(selectSelectedTypeOfGroup);
    const typeOfGroupSubType = useSelector(selectSelectedSubType);
    const othersTypeOfGroupValue = useSelector(selectOtherSpecificationToG);
    const hasOtherToG = useCurrentOtherInput(OTHERS_INPUT_IDS.typeOfGroup, 'tog');
    const isEmployerEmployeeToG = useCurrentSubType(SUB_TYPE_IDS.employerEmployee);
    const isOFWToG = useCurrentSubType(SUB_TYPE_IDS.OFW);
    const hasSubTypeToG =  isEmployerEmployeeToG || isOFWToG
    // AGE PROFILE
    const ageProfile = useSelector(selectAgeProfile);
    const ageProfileFormat = ageProfile?.map(a => a.age).join(' - ') || '';
    const minAgeProfile = useSelector(selectMinAge);
    const maxAgeProfile = useSelector(selectMaxAge);
    // MODE OF PAYMENT
    const modeOfPayment = useSelector(selectFundingArrangementsState);
    // PROPOSAL TYPE (CUSTOMIZE/PROTOTYPE)
    const proposalTypeState = useSelector(selectProposalTypeState);
    const isPrototypeTypeComplete = useSelector(selectIsPrototypeTypeComplete);
    const isCustomize = useCurrentProposalType(PROPOSAL_TYPE_IDS.customized)
    const isPrototypeProposal = useCurrentProposalType(PROPOSAL_TYPE_IDS.prototype)
    const isProposalTypeComplete = useSelector(selectIsProposalTypeComplete);
    const isPrototype = isPrototypeProposal && isProposalTypeComplete;
    // PROTOTYPE
    const prototype = useSelector(selectPrototypeTypeState);
    // PRODUCT
    const product = useSelector(selectProductTypeState);
    const isProductTypeComplete = useSelector(selectIsProductTypeComplete);
    const isProductGPA = useCurrentProduct(PRODUCT_TYPE_IDS.GPA) // age
    const isProductGCLI = useCurrentProduct(PRODUCT_TYPE_IDS.GCLI) // months
    const isProductGYRT = useCurrentProduct(PRODUCT_TYPE_IDS.GYRT) // age
    const isProductGCI = useCurrentProduct(PRODUCT_TYPE_IDS.GCI)  // age
    const hasNo6680Rates = isProductGCI || isProductGPA
    const isRatesIsMonthType = isProductGCLI;
    const isRatesIsAgeType = isProductGPA || isProductGYRT || isProductGCI;
    const isProductGYRTOrProductGPA = isProductGYRT || isProductGPA || isProductGCI;
    // const isProductGYRTOrProductGPAOrProductGCI = isProductGYRT || isProductGPA || isProductGCI;
    // BASIC PLANS
    const basicPlan = useSelector(selectBasicPlansState);
    const isBasicPlanComplete = useSelector(selectIsBasicPlansComplete);
    const isGADDPPlan = useCurrentBasicPlanType(BASIC_PLAN_IDS.GADDP.id)
    // COVERAGE
    const coverage = useSelector(selectCoverageTypeState);
    const isCoverageTypeComplete = useSelector(selectIsCoverageTypeComplete);
    const isCoverageTypeSelected: boolean =  (isProductGYRT || isProductGPA || isProductGCI) 
    const isCoverageType = isProductGPA || isProductGYRT || isProductGCI;
    // COVERAGE: LEVEL RANKING
    const inputsByLevelRanking = useSelector(selectLevelRankingInputs);
    const ridersLevelRanking = useSelector(selectLevelRankingRiders);
    const isLevelRanking = useCurrentCoverageType(COVERAGE_TYPE_IDS.levelRanking)
    // COVERAGE: UNIFORM
    const inputsByUniformCoverage = useSelector(selectUniformCoverage);
    const ridersUniformCoverage = useSelector(selectUniformCoverageRiders);
    const isUniformCoverage = useCurrentCoverageType(COVERAGE_TYPE_IDS.uniformCoverage)
    // COVERAGE: SALARY RANKING
    const checkedSalaryRank = useSelector(selectCheckedSalaryRank);
    const inputsBySalaryRank = useSelector(selectSalaryRank);
    const ridersSalaryRank = useSelector(selectSalaryRankRiders);
    const isSalaryRank = useCurrentCoverageType(COVERAGE_TYPE_IDS.salaryRank)
    const isLevelRankingAndSalaryRank = isLevelRanking || isSalaryRank
    // AMOUNT OF LOANS
    const amountOfLoans = useSelector(selectAmountOfLoans);
    const amountOfLoansFAmount = useSelector(selectAmountOfLoansFAmount);
    const isAmountOfLoansComplete = useSelector(selectIsAmountLoansComplete);
    // const isAmountLoansCheckBoxComplete = useSelector(selectIsAmountLoansComplete);
    const inputAmountOfLoans = useSelector(selectAmountOfLoansInput);
    const ridersAmountOfLoans = useSelector(selectAmountOfLoansRiders);
    const minAmountOfLoans = useSelector(selectMinAmount);
    const maxAmountOfLoans = useSelector(selectMaxAmount);
    const fAmountMin = formatNumberWithComma(minAmountOfLoans ?? '');
    const fAmountMax = formatNumberWithComma(maxAmountOfLoans ?? '');
    const formatMinMaxAmount = `${fAmountMin} - ${fAmountMax}`;
    const portfolioOfLoans = useSelector(selectLoanPortfolio);
    const fAmountPortfolio = formatNumberWithComma(portfolioOfLoans ?? '');
    const isAoLAnnually = useCurrentAmountOfLoans(LOAN_AMOUNTS_TYPE_IDS.annually)
    const isAoLInitial = useCurrentAmountOfLoans(LOAN_AMOUNTS_TYPE_IDS.initial)
    const isAoLOutstanding = useCurrentAmountOfLoans(LOAN_AMOUNTS_TYPE_IDS.outstanding)
    const isAmountOfLoansSelected: boolean = isProductGCLI 
    // SELECTED RIDERS
    const riders = useSelector(selectedRidersState);
    const ridersBMSR = useSelector(selectedBMSRState);
    // PAYMENT TERM
    const paymentTerm = useSelector(selectPaymentTerm)
    // const paymentTermState = useSelector(selectPaymentTermNameState)
    // MATURITY MONTHS
    const maturityMonths = useSelector(selectLoanMaturityMonthsState);
    const maturityMonthsFormat = formatNumberWithComma(maturityMonths ?? '');
    // Loan Maturity
    const loanMaturity = useSelector(selectLoanMaturityTypeState);
    // COMMISSION AND SERVICES
    const commission = useSelector(selectCommissionValueState);
    const services = useSelector(selectServiceValueState);
    // BORROWERS
    const borrowerAgeOptionsList = useSelector(borrowerAgeOptionsState)
    const borrowerAge6670 = useSelector(selected6670State);
    const borrowerAge6670Amount = useSelector(selected6670Amount);
    const borrowerAge7175 = useSelector(selected7175State);
    const borrowerAge7175Amount = useSelector(selected7175Amount);
    const borrowerAge7680 = useSelector(selected7680State);
    const borrowerAge7680Amount = useSelector(selected7680Amount);
    // NOTES (CFE)
    const notesCFE = useSelector(selectNotesState);
    // FILE (CFE)
    const filesCFE = useSelector(selectFiles);

    const isHasAttachedFile = isProductGPA || isProductGYRT;
    const isNoRates = riders.length === 0 && (isProductGYRT || isProductGPA);
    
    const isShowPaymentTermAndLoanMaturity: boolean = 
        isProductTypeComplete 
        && isBasicPlanComplete 
        && isAmountOfLoansComplete;
        
    const isGADDPProduct = isCustomize &&  isProductGPA && basicPlan?.id === BASIC_PLAN_TYPE_IDS.GADDP

    const showAmountOfLoansInput: boolean = 
        isProductGCLI 
        && isBasicPlanComplete 
        && isAmountOfLoansComplete;

    const showCheckboxInputAmountLoans = 
        isProductGCLI 
        && isProductTypeComplete;

    const showCheckboxInputCoverageType = 
        isProductTypeComplete 
        && [PRODUCT_TYPE_IDS.GPA, PRODUCT_TYPE_IDS.GYRT].includes(product?.id ?? 0);

    const isShowAttachableRiders: boolean = 
        // !isProductGCI &&
        isProductTypeComplete 
        && isBasicPlanComplete 
        && (isCoverageTypeComplete || isAmountOfLoansComplete);

    const isShowAmountOfLoans: boolean = 
        isProductGCLI 
        && isBasicPlanComplete

    const isShowCoverageType: boolean = 
        (isProductGPA  ||  isProductGYRT || isProductGCI)
        && isBasicPlanComplete

    const isShowRates: boolean = 
        isProductTypeComplete
        && isBasicPlanComplete
        && (isCoverageTypeComplete || isAmountOfLoansComplete)

    const isShowAttachedFile = 
        isShowCoverageType 
        && (isLevelRanking || isUniformCoverage || isSalaryRank)

    // POLICY INFO
    const isGCLIPOutstandingPolicy = isProductGCLI && isAoLOutstanding
    const isGCLIPPrincipalPolicy = isProductGCLI && (isAoLAnnually || isAoLInitial)
    const isGADDPolicy = isProductGPA && isGADDPPlan
    const isGCLIPolicy = isGCLIPOutstandingPolicy || isGCLIPPrincipalPolicy
    const isGTLIPolicy = isGADDPolicy

    const PSAmountInsurance = isGCLIPOutstandingPolicy || isGCLIPPrincipalPolicy
    const PSCoveragePeriod = isGCLIPOutstandingPolicy || isGCLIPPrincipalPolicy
    const PSPremiums = isGCLIPOutstandingPolicy || isGCLIPPrincipalPolicy || isGADDPolicy
    const PSPremiumRates = isGCLIPOutstandingPolicy || isGCLIPPrincipalPolicy
    const PSRefundPremiums = isGCLIPOutstandingPolicy || isGCLIPPrincipalPolicy
    const PSTerminationAge = isGCLIPOutstandingPolicy || isGCLIPPrincipalPolicy

    const PIEligibleIndividuals = isGCLIPOutstandingPolicy || isGCLIPPrincipalPolicy || isGADDPolicy
    const PIClassificationAndEligibility = isGCLIPOutstandingPolicy || isGCLIPPrincipalPolicy || isGADDPolicy
    const PIParticipationReq = isGCLIPOutstandingPolicy || isGCLIPPrincipalPolicy || isGADDPolicy
    const PIUnderwritingProvisions = isGCLIPOutstandingPolicy || isGCLIPPrincipalPolicy
    const PISpecialUnderwritingProvisions = isGADDPolicy

    // SPECIAL UNDERWRITING PROVISIONS
    const PSUPEnrollment = isGCLIPPrincipalPolicy || isGCLIPOutstandingPolicy
    const PSUPRollOverProvisions = isGCLIPPrincipalPolicy || isGCLIPOutstandingPolicy
    const PSUPTermination = isGCLIPPrincipalPolicy || isGCLIPOutstandingPolicy
    const PSUPGenDefinitions = isGCLIPPrincipalPolicy || isGCLIPOutstandingPolicy
    const PSUPFaceAmountInsurance = isGCLIPPrincipalPolicy
    const PSUPPremiumComputation = isGCLIPPrincipalPolicy
    const PSUPClaimsProcedure = isGCLIPPrincipalPolicy || isGCLIPOutstandingPolicy
    const PSUPNonCoverageProvisions = isGCLIPPrincipalPolicy

    return {
        // STEPS
        proposalStep,

        // CHANNEL TYPE
        channelType,
        channelTypeSubName,
        channelTypeNumber,
        channelTypeEmail,
        showInputChannelType,

        // PROPOSAL AS
        proposalAs,
        isForRenew,

        // RENEWAL
        forRenew,
        registeredNameForRenew,
        registeredNameExpiredAt,

        // CURRENT GROUP NAME
        currentGroupName,
        // CURRENT STATUS
        currentStatusName,
        isPendingCurrentProposalStatus,

        // EFFECTIVE DATE
        effectiveDateGMS,

        // BASIC INFORMATION
        basicInformation,
        groupName,
        noOfLives,
        companyMobileNo,
        companyTelephone,
        completeBusinessAddress,
        proposalAddress,
        email,
        addressDesignation,
        designation,
        contactPersonSalutation,
        contactPersonFirstName,
        contactPersonMiddleName,
        contactPersonLastName,
        contactNumber,
        contactTelephone,
        tinNumber,
        secNumber,
        companyTypeNumber,
        companyContactNumber,
        contactPersonNumber,
        isOpenTypeOfOption,
        affiliation,
        hasAffiliation,
        natureOfBusinessCategory,
        natureOfBusinessItem,

        // GROUP CLASSIFICATION
        groupClassification,
        othersGroupClassificationValue,
        hasOtherSpecifyGC,
        hasOtherSpecifyBT,

        // TYPE OF GROUP
        typeOfGroup,
        typeOfGroupSubType,
        othersTypeOfGroupValue,
        hasOtherToG,
        isEmployerEmployeeToG,
        isOFWToG,
        hasSubTypeToG,

        // AGE PROFILE
        ageProfile,
        ageProfileFormat,
        minAgeProfile,
        maxAgeProfile,

        // MODE OF PAYMENT
        modeOfPayment,

        // PROPOSAL TYPE
        proposalTypeState,
        isPrototypeTypeComplete,
        isProposalTypeComplete,
        isCustomize,
        isPrototypeProposal,
        isPrototype,

        // PROTOTYPE
        prototype,

        // PRODUCT
        product,
        isProductTypeComplete,
        isProductGPA,
        isProductGCLI,
        isProductGYRT,
        isProductGCI,
        hasNo6680Rates,
        isRatesIsMonthType,
        isRatesIsAgeType,
        isGADDPProduct,
        isProductGYRTOrProductGPA,
        // isProductGYRTOrProductGPAOrProductGCI,
        // BASIC PLANS
        basicPlan,
        isBasicPlanComplete,
        isGADDPPlan,

        // COVERAGE
        coverage,
        isCoverageTypeComplete,
        isCoverageTypeSelected,
        isCoverageType,

        // COVERAGE: LEVEL RANKING
        inputsByLevelRanking,
        ridersLevelRanking,
        isLevelRanking,

        // COVERAGE: UNIFORM
        inputsByUniformCoverage,
        ridersUniformCoverage,
        isUniformCoverage,

        // COVERAGE: SALARY RANKING
        checkedSalaryRank,
        inputsBySalaryRank,
        ridersSalaryRank,
        isSalaryRank,
        isLevelRankingAndSalaryRank,

        // AMOUNT OF LOANS
        amountOfLoans,
        amountOfLoansFAmount,
        isAmountOfLoansComplete,
        // isAmountLoansCheckBoxComplete,
        inputAmountOfLoans,
        ridersAmountOfLoans,
        minAmountOfLoans,
        maxAmountOfLoans,
        fAmountMin,
        fAmountMax,
        formatMinMaxAmount,
        portfolioOfLoans,
        fAmountPortfolio,
        isAoLAnnually,
        isAoLInitial,
        isAoLOutstanding,
        isAmountOfLoansSelected,

        // RIDERS
        riders,
        ridersBMSR,

        // PAYMENT TERM
        paymentTerm,
        // paymentTermState,

        // MATURITY
        maturityMonths,
        maturityMonthsFormat,
        // LOAN MATURITy
        loanMaturity,
        // COMMISSION / SERVICES
        commission,
        services,

        // BORROWERS
        borrowerAgeOptionsList,
        borrowerAge6670,
        borrowerAge6670Amount,
        borrowerAge7175,
        borrowerAge7175Amount,
        borrowerAge7680,
        borrowerAge7680Amount,
        // NOTES / FILES
        notesCFE,
        filesCFE,

        // DEPARTMENT
        isITDepartment,
        isActuarialDepartment,
        isMarketingGMSDepartment,
        isEBAMDepartment,
        // STATUS: MARKETING GMS
        isBookedProposal,
        isReleaseProposal,
        isDraftProposal,
        isPendingProposal,
        isClosedProposal,
        isAmendGMSProposal,
        isAmendProposal,
        isProposalApproved,
        // STATUS: ACtUARIAL
        isPassedComputation,
        isDeniedComputation,
        isQueuedComputation,
        isEndorsedComputation,
        isForApprovalComputation,
        isForReApprovalComputation,
        isForRatingComputation,
        isForAmendingComputation,
        // STATUS: POLICY EMBAM
        isContractCreationPolicy,
        isContractInProgressPolicy,
        isForContractReviewPolicy,
        isRevisionPolicy,
        isReadyForIssuancePolicy,
        isIssuedPolicy,
        // DISPLAY FLAGS
        isHasAttachedFile,
        isNoRates,
        isShowPaymentTermAndLoanMaturity,
        showAmountOfLoansInput,
        showCheckboxInputAmountLoans,
        showCheckboxInputCoverageType,
        isShowAttachableRiders,
        isShowAmountOfLoans,
        isShowCoverageType,
        isShowRates,
        isShowAttachedFile,

        // POLICY TYPE
        isGCLIPOutstandingPolicy,
        isGCLIPPrincipalPolicy,
        isGADDPolicy,
        isGCLIPolicy,
        isGTLIPolicy,

        PSAmountInsurance,
        PSCoveragePeriod,
        PSPremiums,
        PSPremiumRates,
        PSRefundPremiums,
        PSTerminationAge,
        
        PIEligibleIndividuals,
        PIClassificationAndEligibility,
        PIParticipationReq,
        PIUnderwritingProvisions,
        PISpecialUnderwritingProvisions,
        // SPECIAL UNDERWRITING PROVISIONS
        PSUPEnrollment,
        PSUPRollOverProvisions,
        PSUPTermination,
        PSUPGenDefinitions,
        PSUPFaceAmountInsurance,
        PSUPPremiumComputation,
        PSUPClaimsProcedure,
        PSUPNonCoverageProvisions,
    };
};